import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Check,
  Loader2,
  Plus,
  Power,
  Shield,
  ShieldOff,
  Trash2,
  UserPlus,
} from "lucide-react";
import { money } from "../lib/format";
import { api } from "../lib/api";

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    store_name: "",
    store_number: "",
    contact_name: "",
    phone: "",
    is_active: true,
  });
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const data = await api.listUsers();
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setActive = async (id, val) => {
    try {
      await api.patchUser(id, { is_active: val });
      toast.success(val ? "User enabled" : "User deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this user and all of their data?")) return;
    try {
      await api.deleteUser(id);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const create = async () => {
    setCreating(true);
    try {
      await api.createUser({
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      toast.success("User created");
      setShowCreate(false);
      setForm({
        email: "",
        password: "",
        store_name: "",
        store_number: "",
        contact_name: "",
        phone: "",
        is_active: true,
      });
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Create failed (email may already exist)",
      );
    } finally {
      setCreating(false);
    }
  };

  if (!users) {
    return (
      <div className="text-sm text-[#525252] flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
      </div>
    );
  }

  return (
    <div data-testid="admin-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Administration</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">
            Store directory
          </h1>
          <p className="text-sm text-[#525252] mt-2">
            Approve signups, deactivate stores, and monitor AI usage.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-md hover:bg-[#171717]"
          data-testid="show-create-user-btn"
        >
          <UserPlus className="w-4 h-4" /> {showCreate ? "Cancel" : "Add store"}
        </button>
      </div>

      {showCreate && (
        <div className="bg-white border border-[#E5E5E5] rounded-md p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} />
          <Input label="Temporary password" v={form.password} on={(v) => setForm({ ...form, password: v })} type="password" />
          <Input label="Store name" v={form.store_name} on={(v) => setForm({ ...form, store_name: v })} />
          <Input label="Store number" v={form.store_number} on={(v) => setForm({ ...form, store_number: v })} />
          <Input label="Contact name" v={form.contact_name} on={(v) => setForm({ ...form, contact_name: v })} />
          <Input label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} />
          <button
            onClick={create}
            disabled={creating}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-md disabled:opacity-50"
            data-testid="create-user-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create user
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <table className="w-full data-table text-sm" data-testid="users-table">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left">Store</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">AI calls</th>
              <th className="px-4 py-3 text-right">Tokens</th>
              <th className="px-4 py-3 text-right">Spent (USD)</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className="border-t border-[#E5E5E5]"
                data-testid={`user-row-${u.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-[13px]">{u.store_name || "—"}</div>
                  <div className="text-[11px] text-[#525252] font-mono-tabular">
                    #{u.store_number || "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-[12px]">
                  {u.contact_name || "—"}
                  <div className="text-[11px] text-[#525252] font-mono-tabular">
                    {u.phone || ""}
                  </div>
                </td>
                <td className="px-4 py-3 text-[12px] font-mono-tabular">
                  {u.email}
                  {u.role === "admin" && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#0A0A0A] border border-[#0A0A0A] rounded-sm px-1.5">
                      admin
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]">
                      <Check className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-[#FEFCE8] text-[#CA8A04] border border-[#FEF08A]">
                      Pending / disabled
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-tabular">
                  {u.usage?.calls ?? 0}
                </td>
                <td className="px-4 py-3 text-right font-mono-tabular text-[12px]">
                  {((u.usage?.input_tokens ?? 0) + (u.usage?.output_tokens ?? 0)).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right font-mono-tabular">
                  {money(u.usage?.cost_usd ?? 0)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.role !== "admin" && (
                      <>
                        {u.is_active ? (
                          <button
                            onClick={() => setActive(u.id, false)}
                            className="p-1.5 rounded-sm hover:bg-[#FFF0F0] text-[#DC2626]"
                            title="Deactivate"
                            data-testid={`deactivate-${u.id}`}
                          >
                            <ShieldOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setActive(u.id, true)}
                            className="p-1.5 rounded-sm hover:bg-[#F0FDF4] text-[#16A34A]"
                            title="Approve / enable"
                            data-testid={`activate-${u.id}`}
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => remove(u.id)}
                          className="p-1.5 rounded-sm hover:bg-[#FFF0F0] text-[#DC2626]"
                          title="Delete"
                          data-testid={`delete-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, v, on, type = "text" }) {
  return (
    <div>
      <label className="block eyebrow mb-2">{label}</label>
      <input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="w-full border border-[#E5E5E5] rounded-md px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none"
      />
    </div>
  );
}
